import React, { ComponentType, FC } from 'react';
import styled from 'styled-components';

import {
  SHOP_LINKS,
  USEFUL_LINKS,
  MFB_CONTACT_US_LINK,
  MFB_PRIVACY_POLICY_LINK,
  MFB_TERMS_CONDITIONS_LINK,
  SOCIAL_LINKS,
  APP_STORE_LINKS,
} from './constants/urls';
import { AppStoreIcon } from './svgs/AppStoreIcon';
import { FacebookIcon } from './svgs/FacebookIcon';
import { GooglePlayIcon } from './svgs/GooglePlayIcon';
import { InstagramIcon } from './svgs/InstagramIcon';
import { XIcon } from './svgs/XIcon';
import { YoutubeIcon } from './svgs/YoutubeIcon';

export interface LinkProps {
  href?: string;
  children: string;
}

export interface MarketingSiteFooterProps {
  Link: ComponentType<LinkProps>;
  IsShieldVisible?: boolean;
}

export const MarketingSiteFooter: FC<MarketingSiteFooterProps> = (props) => {
  const currentYear = new Date().getFullYear();
  const Link = props.Link;

  return (
    <Footer className="container">
      <LinksContainer>
        <div>
          <Header>Useful Links</Header>
          <ul>
            {USEFUL_LINKS.map((link) => (
              <Li tabIndex={0} aria-label={link.label} key={link.label}>
                <Link href={link.href}>{link.label}</Link>
              </Li>
            ))}
          </ul>
        </div>
        <div>
          <Header>Shop</Header>
          <ul>
            {SHOP_LINKS.map((link) => (
              <Li tabIndex={0} aria-label={link.label} key={link.label}>
                <Link href={link.href}>{link.label}</Link>
              </Li>
            ))}
          </ul>
        </div>
      </LinksContainer>
      <AppLinksContainer>
        <Header>Download our app</Header>
        <div className={'__svg-container'}>
          <AppStoreIcon href={APP_STORE_LINKS.APPLE_STORE} className={'__svg'} />
          <GooglePlayIcon href={APP_STORE_LINKS.GOOGLE_PLAY_STORE} className={'__svg'} />
        </div>
      </AppLinksContainer>
      <CopyrightInfoContainer>
        <p>
          {` © ${currentYear} My Food Bag Ltd. All Rights Reserved`} -{' '}
          <Link href={MFB_TERMS_CONDITIONS_LINK}>Terms & Conditions</Link> -{' '}
          <Link href={MFB_PRIVACY_POLICY_LINK}>Privacy Policy</Link> -{' '}
          <Link href={MFB_CONTACT_US_LINK}>Contact Us</Link>
        </p>
        <div className={'__svg-container'}>
          <FacebookIcon href={SOCIAL_LINKS.FACEBOOK} />
          <InstagramIcon href={SOCIAL_LINKS.INSTAGRAM} />
          <XIcon href={SOCIAL_LINKS.X} />
          <YoutubeIcon href={SOCIAL_LINKS.YOUTUBE} />
          {props.IsShieldVisible && (
            <button
              id="shielded-logo"
              className="btn btn-link p-0"
              title="Open Women's Refuge private in-browser widget"
              style={{ cursor: 'pointer' }}
            >
              <img
                alt="shielded"
                src="https://shielded.co.nz/img/custom-logo.png"
                height="40"
                width="40"
              />
            </button>
          )}
        </div>
      </CopyrightInfoContainer>
    </Footer>
  );
};

const Footer = styled.footer`
  padding-top: 3rem;
  padding-bottom: 1rem;
  display: grid;
  grid-template-columns: 0 1fr 1fr 0;
  grid-template-rows: 5fr 1fr;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 15px 1fr 15px;
    grid-template-rows: auto auto auto;
    row-gap: 24px;
  }

  li {
    list-style: none;

    &:hover {
      opacity: 0.6;
      cursor: pointer;
      transition: all ease-in-out 150ms;
    }
  }

  a {
    text-decoration: none;
  }

  h2,
  a,
  p,
  span {
    margin: 0;
    padding: 0;
    font-family: Mulish;
    font-weight: 400;
  }

  * {
    padding: 0;
    margin: 0;
  }

  *:focus-visible {
    outline-offset: 4px;
    outline: #2f80ed solid 2px;
  }
`;

const Header = styled.h2`
  font-weight: 800 !important;
  font-size: 22px !important;
  margin-bottom: 8px !important;
  color: white;
`;

const LinksContainer = styled.section`
  grid-column-start: 2;
  display: flex;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  width: fit-content;
  gap: 48px;
`;

const AppLinksContainer = styled.section`
  grid-column-start: 3;
  align-self: flex-start;

  @media (max-width: 768px) {
    grid-column-start: 2;
    grid-row-start: 2;
  }

  .__svg-container {
    width: 100%;
    display: flex;
    gap: 8px;
  }

  .__svg {
    width: 100%;
    display: flex;
    justify-content: center;
    align-self: center;
    border: 2px solid #848484;
    border-radius: 4px;
    padding: 16px 32px;

    &:hover {
      opacity: 0.6;
      cursor: pointer;
      transition: all ease-in-out 150ms;
    }
  }
`;

const CopyrightInfoContainer = styled.section`
  grid-row-start: 2;
  grid-column-start: 2;
  grid-column-end: 4;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: 768px) {
    grid-column-start: 2;
    grid-row-start: 3;
    grid-column-end: 2;
    flex-direction: column;
    gap: 16px;
  }

  p {
    color: #848484;
    font-size: 15px !important;

    a {
      color: #848484;

      &:hover {
        opacity: 0.6;
        cursor: pointer;
        transition: all ease-in-out 150ms;
      }
    }
  }

  .__svg-container {
    display: flex;
    flex-direction: row;
    gap: 16px;
    align-items: baseline;

    svg {
      &:hover {
        opacity: 0.6;
        cursor: pointer;
        transition: all ease-in-out 150ms;
      }
    }
  }
`;

const Li = styled.li`
  margin-bottom: 6px !important;

  a {
    font-size: 18px !important;
    color: #77a222;
  }
`;
